import React, { useEffect, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { fluid } from '../styles/motion'; // [FLUID-3 2026-10-12]

/**
 * App-wide glass dialog service. Replaces the native `window.confirm` /
 * `window.alert` (which render an un-styled browser box pinned to the top of
 * the window) with the centered, liquid-glass `ConfirmDialog` used everywhere
 * else in the app.
 *
 * Usage:
 *   if (await confirmDialog({ message: 'Delete this?', destructive: true })) { ... }
 *   await alertDialog({ message: 'Something went wrong.' });
 *
 * `<DialogHost />` must be mounted once near the app root (inside the
 * ThemeProvider) for these to render.
 */

export interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  icon?: string; // font-awesome class, e.g. "fa-trash-alt"
}

export interface AlertOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  icon?: string;
}

interface DialogRequest {
  id: number;
  kind: 'confirm' | 'alert';
  options: ConfirmOptions & AlertOptions;
  resolve: (value: boolean) => void;
}

// A single host subscribes; imperative calls push requests to it.
let pushRequest: ((req: DialogRequest) => void) | null = null;
let counter = 0;

function enqueue(kind: 'confirm' | 'alert', options: ConfirmOptions & AlertOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const req: DialogRequest = { id: ++counter, kind, options, resolve };
    if (pushRequest) {
      pushRequest(req);
    } else {
      // Host not mounted (shouldn't happen in-app). Fail safe: don't silently
      // proceed on a confirm; resolve an alert as acknowledged.
      // eslint-disable-next-line no-console
      console.warn('DialogHost is not mounted — dialog skipped.');
      resolve(kind === 'alert');
    }
  });
}

/** Centered glass confirm. Resolves true on confirm, false on cancel/dismiss. */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return enqueue('confirm', options);
}

/** Centered glass alert with a single acknowledge button. */
export function alertDialog(options: AlertOptions): Promise<void> {
  return enqueue('alert', options).then(() => undefined);
}

export const DialogHost: React.FC = () => {
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    pushRequest = (req) => setQueue((q) => [...q, req]);
    return () => { pushRequest = null; };
  }, []);

  const current = queue[0];

  // Open the panel whenever a new request reaches the front of the queue.
  useEffect(() => {
    if (current) setOpen(true);
  }, [current]);

  if (!current) return null;

  const isAlert = current.kind === 'alert';

  // Resolve, play the exit transition, then drop the request from the queue.
  // [FLUID-3 2026-10-12, user] "Nothing waits on the motion": the answer goes to the caller AT the click (the
  // delete / save it guards starts at once) and only the 0.18 s close plays afterwards. It used to resolve after
  // the 200 ms exit. A dialog asked for in the meantime waits in the queue until this one has gone.
  const finish = (result: boolean) => {
    setOpen(false);
    current.resolve(result);
    window.setTimeout(() => {
      setQueue((q) => q.slice(1));
    }, fluid.dialogOutMs);
  };

  const fallbackIcon = isAlert
    ? (current.options.destructive ? 'fa-circle-exclamation' : 'fa-circle-info')
    : undefined;

  return (
    <ConfirmDialog
      liquid /* [LIQUID-GLASS 2026-11-02] the glass confirm window */
      open={open}
      title={current.options.title ?? (isAlert ? 'Notice' : 'Confirm')}
      message={current.options.message}
      confirmLabel={current.options.confirmLabel ?? (isAlert ? 'OK' : 'Confirm')}
      cancelLabel={current.options.cancelLabel}
      destructive={current.options.destructive}
      icon={current.options.icon ?? fallbackIcon}
      hideCancel={isAlert}
      onConfirm={() => finish(true)}
      onCancel={() => finish(false)}
    />
  );
};
